import { useAuthStore } from '~/stores/useAuthStore';
import { useSessionExpiredStore } from '~/stores/useSessionExpiredStore';

/**
 * 로그인 세션 유휴 타임아웃 → 자동 로그아웃
 *
 * AS-IS layout.jsp 의 checkSessionTime / initSessionTime / doLogout 로직을 TO-BE(Vue)로 이식한 것.
 * - AS-IS 와 동일하게, "서버(DB)로 요청이 오갈 때"만 유휴 타이머를 리셋한다.
 *   (AS-IS common.js 는 메뉴 이동/AJAX 시 initSessionTime() 을 호출한다.)
 *   → TO-BE 에서는 useApi 응답 훅에서 markSessionActivity() 를 호출한다.
 *   → WebSocket keepalive 등 useApi 를 타지 않는 백그라운드 연결은 타이머를 리셋하지 않으므로,
 *     소켓이 서버 세션을 살려두어도 사용자가 서버 요청을 하지 않으면 반드시 로그아웃된다.
 * - 마지막 서버요청 후 SESSION_TIMEOUT_MS 경과 시, 즉시 로그아웃하지 않고 세션 만료 다이얼로그
 *   (useSessionExpiredStore)를 띄운다. 사용자가 "확인" 을 누르면 /login 으로 이동한다.
 *   (default.vue 의 <UiConfirm> 세션 만료 처리와 동일한 흐름)
 * - 여러 탭이 열려 있어도 마지막 활동시각을 localStorage 로 공유하여, 활동 중인 탭이 있으면
 *   유휴 탭이 먼저 로그아웃되지 않도록 한다.
 */

// AS-IS layout.jsp 는 서버의 session.maxInactiveInterval 을 사용한다.
// web.xml 의 <session-timeout>300</session-timeout>(분) = 5시간 과 값을 일치시킨다.
// (추후 /auth/me 응답에 maxInactiveInterval 이 추가되면 그 값으로 대체 가능)
const SESSION_TIMEOUT_MS = 5 * 60 * 60 * 1000; // 5시간

const ACTIVITY_STORAGE_KEY = 'sgate:lastActivityTime';
const CHECK_INTERVAL_MS = 1000; // layout.jsp 와 동일하게 1초 tick
const ACTIVITY_THROTTLE_MS = 1000; // 활동 기록 과다 쓰기 방지

let started = false;
let intervalId: ReturnType<typeof setInterval> | null = null;
let lastWriteTs = 0;

/** localStorage 에서 마지막 활동시각(ms) 조회 (탭 간 공유) */
const readLastActivity = (): number => {
  try {
    const v = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) ? n : Date.now();
  } catch {
    return Date.now();
  }
};

/** localStorage 에 마지막 활동시각 기록 */
const writeLastActivity = (ts: number) => {
  try {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(ts));
  } catch {
    // storage 접근 불가(private mode 등) 시 무시
  }
};

/**
 * 서버(DB) 요청이 오갈 때마다 호출하여 유휴 타이머를 리셋한다. — layout.jsp 의 initSessionTime 대응.
 * useApi 의 onResponse / onResponseError 에서 호출된다.
 * (WebSocket 등 useApi 를 타지 않는 백그라운드 연결은 여기로 들어오지 않으므로 타이머를 리셋하지 않는다.)
 */
export const markSessionActivity = () => {
  if (!import.meta.client) return;
  const now = Date.now();
  if (now - lastWriteTs < ACTIVITY_THROTTLE_MS) return; // 초당 1회로 throttle
  lastWriteTs = now;
  writeLastActivity(now);
};

/** 1초마다 유휴시간 확인 — layout.jsp 의 checkSessionTime 대응 */
const checkTimeout = () => {
  const auth = useAuthStore();
  if (!auth.isLoggedIn) return;

  if (Date.now() - readLastActivity() >= SESSION_TIMEOUT_MS) {
    stopSessionTimeout();

    // 1) 세션 만료 다이얼로그 표시 (확인 클릭 시 handleConfirm 이 /login 으로 이동)
    const sessionExpiredStore = useSessionExpiredStore();
    if (!sessionExpiredStore.isVisible) {
      sessionExpiredStore.show();
    }

    // 2) 서버 세션 무효화 → 다이얼로그 상태에서 새로고침해도 재로그인되지 않게 한다.
    //    (소켓 keepalive 로 서버 세션이 살아있어 새로고침 시 checkAuth 가 성공하던 문제 차단)
    auth.invalidateServerSession();
  }
};

/** 세션 유휴 타임아웃 감시 시작 (로그인 상태에서 default.vue 가 호출) */
export const startSessionTimeout = () => {
  if (!import.meta.client || started) return;
  started = true;

  // 시작(로그인/새로고침)도 서버요청 직후이므로 활동으로 간주하여 기준시각 갱신
  lastWriteTs = Date.now();
  writeLastActivity(lastWriteTs);

  intervalId = window.setInterval(checkTimeout, CHECK_INTERVAL_MS);
};

/** 세션 유휴 타임아웃 감시 중지 (로그아웃/로그인 페이지/언마운트 시) */
export const stopSessionTimeout = () => {
  if (!import.meta.client || !started) return;
  started = false;

  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};
